import { useState } from 'react'
import { motion } from 'framer-motion'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

export default function Enterprise() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    executiveCount: '',
    message: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)

    // For now, send via mailto - can be replaced with API endpoint
    const subject = encodeURIComponent(`Enterprise Inquiry: ${formData.companyName}`)
    const body = encodeURIComponent(`
Company: ${formData.companyName}
Contact: ${formData.contactName}
Email: ${formData.contactEmail}
Phone: ${formData.contactPhone}
Number of Executives: ${formData.executiveCount}

Message:
${formData.message}
    `)

    window.location.href = `mailto:enterprise@monadnockcyber.ai?subject=${subject}&body=${body}`

    setLoading(false)
    setSubmitted(true)
  }

  const pricing = [
    { count: '1-4 executives', price: '$2,500/person', discount: 'Standard pricing' },
    { count: '5-9 executives', price: '$2,250/person', discount: '10% volume discount' },
    { count: '10-24 executives', price: '$2,000/person', discount: '20% volume discount' },
    { count: '25+ executives', price: 'Custom', discount: 'Contact for enterprise pricing' },
  ]

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-ginger-500 font-semibold text-sm mb-2">ENTERPRISE SOLUTIONS</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Protect Your <span className="text-ginger-500">Executive Team</span>
            </h1>
            <p className="text-dark-300 text-lg max-w-2xl mx-auto">
              Your executives are targets. Their exposed data is your company's risk.
              We assess your entire leadership team and help you close the gaps.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-12 px-4 bg-dark-900/50">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-ginger-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ginger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="font-bold text-lg mb-2">Bulk Executive Assessment</h3>
            <p className="text-dark-400 text-sm">
              Assess your entire C-suite and board. One invoice, consolidated reporting, unified remediation plan.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-ginger-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ginger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="font-bold text-lg mb-2">Dedicated Analyst Support</h3>
            <p className="text-dark-400 text-sm">
              A named analyst reviews every report, provides threat briefings, and answers questions throughout the engagement.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-ginger-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ginger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="font-bold text-lg mb-2">Executive Summary Reports</h3>
            <p className="text-dark-400 text-sm">
              Board-ready summaries showing organization-wide exposure, risk trends, and remediation progress.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Volume Pricing</h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-4 px-6 text-dark-400 font-medium">Executive Count</th>
                  <th className="text-left py-4 px-6 text-dark-400 font-medium">Price Per Person</th>
                  <th className="text-left py-4 px-6 text-dark-400 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {pricing.map((tier, i) => (
                  <tr key={i} className="border-b border-dark-800">
                    <td className="py-4 px-6 font-medium">{tier.count}</td>
                    <td className="py-4 px-6 text-ginger-500 font-bold">{tier.price}</td>
                    <td className="py-4 px-6 text-dark-400">{tier.discount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-4 bg-dark-800/50 rounded-lg border border-dark-700">
            <p className="text-dark-300 text-sm">
              <strong className="text-white">What's included:</strong> Full Executive Intelligence Report for each person,
              human analyst review, 30-min threat briefing per executive, organization-wide executive summary,
              and 90 days of priority support.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-4 bg-dark-900/50">
        <div className="max-w-xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-2">Request Enterprise Assessment</h2>
          <p className="text-dark-400 text-center mb-8">
            Tell us about your organization and we'll customize a solution.
          </p>

          {submitted ? (
            <div className="card text-center">
              <div className="text-4xl mb-4">&#9989;</div>
              <h3 className="text-xl font-bold mb-2">Request Received</h3>
              <p className="text-dark-400">
                Our enterprise team will contact you within 24 hours to discuss your requirements.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name *</label>
                  <input
                    type="text"
                    className="input"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Number of Executives *</label>
                  <select
                    className="input"
                    required
                    value={formData.executiveCount}
                    onChange={(e) => setFormData({ ...formData, executiveCount: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="1-4">1-4</option>
                    <option value="5-9">5-9</option>
                    <option value="10-24">10-24</option>
                    <option value="25-49">25-49</option>
                    <option value="50+">50+</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Your Name *</label>
                  <input
                    type="text"
                    className="input"
                    required
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Title</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., CISO, VP Security"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    className="input"
                    required
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    className="input"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tell us about your needs</label>
                <textarea
                  className="input min-h-[100px]"
                  placeholder="Any specific concerns, timeline requirements, or questions..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Submitting...' : 'Request Enterprise Quote'}
              </button>

              <p className="text-dark-500 text-xs text-center">
                We typically respond within 24 hours. No spam, ever.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Common Use Cases</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="font-bold text-lg mb-2">Pre-IPO Due Diligence</h3>
              <p className="text-dark-400 text-sm">
                Identify executive exposure risks before they become investor concerns.
                Demonstrate proactive security posture to underwriters and board.
              </p>
            </div>
            <div className="card">
              <h3 className="font-bold text-lg mb-2">M&A Target Assessment</h3>
              <p className="text-dark-400 text-sm">
                Assess the digital footprint of acquisition target leadership.
                Understand inherited risks before deal close.
              </p>
            </div>
            <div className="card">
              <h3 className="font-bold text-lg mb-2">Annual Security Review</h3>
              <p className="text-dark-400 text-sm">
                Make executive privacy assessment part of your annual security program.
                Track year-over-year improvements and emerging risks.
              </p>
            </div>
            <div className="card">
              <h3 className="font-bold text-lg mb-2">Incident Response</h3>
              <p className="text-dark-400 text-sm">
                After a breach or threat, quickly assess executive exposure.
                Prioritize remediation and prepare for potential targeting.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
